import * as THREE from 'three';
import { SKIN_VOLUME } from './volume-skin.js';

interface Vertex {
  point: THREE.Vector3;
  normal: THREE.Vector3;
  weights: Map<number, number>;
  volume: number;
  value: number;
}

/** Cut fitted cloth from the real branching skin, interpolating the rig at the cut edges. `bound` also cuts by a per-vertex value, keeping where it is below `below`. */
export function cutBodyGarment(body: THREE.BufferGeometry, faces: number[], from: number, to: number, gap: number, side = 0, cuts: ((point: THREE.Vector3) => number)[] = [],
  bound?: { values: ArrayLike<number>; below: number }) {
  const position = body.getAttribute('position'), normal = body.getAttribute('normal');
  const bones = body.getAttribute('skinIndex'), weights = body.getAttribute('skinWeight'), volume = body.getAttribute(SKIN_VOLUME);
  const index = body.index!;
  const read = (id: number): Vertex => {
    const amounts = new Map<number, number>();
    for (let c = 0; c < 4; c++) {
      const bone = bones.getComponent(id, c);
      amounts.set(bone, (amounts.get(bone) ?? 0) + weights.getComponent(id, c));
    }
    return { point: new THREE.Vector3().fromBufferAttribute(position, id), normal: new THREE.Vector3().fromBufferAttribute(normal, id),
      volume: volume?.getX(id) ?? 0, weights: amounts, value: bound?.values[id] ?? 0 };
  };
  const interpolate = (a: Vertex, b: Vertex, t: number): Vertex => {
    const amounts = new Map<number, number>();
    for (const [vertex, fraction] of [[a, 1 - t], [b, t]] as const)
      for (const [bone, weight] of vertex.weights) amounts.set(bone, (amounts.get(bone) ?? 0) + weight * fraction);
    return { point: a.point.clone().lerp(b.point, t), normal: a.normal.clone().lerp(b.normal, t).normalize(),
      weights: amounts, volume: THREE.MathUtils.lerp(a.volume, b.volume, t), value: THREE.MathUtils.lerp(a.value, b.value, t) };
  };
  const clip = (polygon: Vertex[], distance: (v: Vertex) => number) => {
    const result: Vertex[] = [];
    polygon.forEach((a, i) => {
      const b = polygon[(i + 1) % polygon.length], da = distance(a), db = distance(b);
      if (da >= 0) result.push(a);
      if ((da < 0) !== (db < 0)) result.push(interpolate(a, b, da / (da - db)));
    });
    return result;
  };
  const positions: number[] = [], normals: number[] = [], indices: number[] = [], amounts: number[] = [], volumes: number[] = [], uvs: number[] = [];
  const emit = (vertex: Vertex) => {
    positions.push(...vertex.point.clone().addScaledVector(vertex.normal, gap).toArray()); normals.push(...vertex.normal.toArray());
    const influence = [...vertex.weights].filter(([, w]) => w > 1e-8).sort((a, b) => b[1] - a[1]).slice(0, 4);
    const sum = influence.reduce((n, [, w]) => n + w, 0);
    for (let c = 0; c < 4; c++) { indices.push(influence[c]?.[0] ?? 0); amounts.push((influence[c]?.[1] ?? 0) / sum); }
    volumes.push(vertex.volume); uvs.push(Math.atan2(vertex.point.z, vertex.point.x) / (2 * Math.PI) + .5, vertex.point.y);
  };
  for (const face of faces) {
    const ys = [0, 1, 2].map(c => position.getY(index.getX(face * 3 + c)));
    if (ys.every(y => y < from) || ys.every(y => y > to)) continue;
    let polygon = [0, 1, 2].map(c => read(index.getX(face * 3 + c)));
    polygon = clip(clip(polygon, v => v.point.y - from), v => to - v.point.y);
    if (side) polygon = clip(polygon, v => v.point.x * side);
    for (const cut of cuts) polygon = clip(polygon, v => cut(v.point));
    if (bound) polygon = clip(polygon, v => bound.below - v.value);
    for (let c = 1; c < polygon.length - 1; c++) {
      const [a, b, d] = [polygon[0], polygon[c], polygon[c + 1]];
      if (b.point.clone().sub(a.point).cross(d.point.clone().sub(a.point)).lengthSq() < 1e-18) continue;
      emit(a); emit(b); emit(d);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(indices, 4));
  geometry.setAttribute('skinWeight', new THREE.Float32BufferAttribute(amounts, 4));
  geometry.setAttribute(SKIN_VOLUME, new THREE.Float32BufferAttribute(volumes, 1));
  return geometry;
}
